import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@/components/ui/Icon'
import type { DismissReason } from '@/types'

export interface DismissReasonOption {
  id: DismissReason
  label: string
  desc: string
  /** Confirmation message shown in the toast after the reason is picked. */
  confirmation: string
}

export const DISMISS_REASONS: readonly DismissReasonOption[] = [
  {
    id: 'wrong-vibe',
    label: 'Not our vibe',
    desc: "Genre or tone doesn't fit us",
    confirmation: "Got it — we'll show fewer like this.",
  },
  {
    id: 'wrong-for-group',
    label: 'Wrong for the group',
    desc: 'Player count or co-op fit is off',
    confirmation: "Got it — we'll be stricter about co-op fit.",
  },
  {
    id: 'low-quality',
    label: 'Quality concerns',
    desc: 'Reviews or polish look off',
    confirmation: "Got it — we'll prefer higher-rated picks.",
  },
  {
    id: 'played-elsewhere',
    label: "We've played it before",
    desc: "Not all of us, but enough that we don't need it again",
    confirmation: "Got it — we won't suggest it again.",
  },
] as const

export const REASON_BY_ID: Record<DismissReason, DismissReasonOption> = Object.fromEntries(
  DISMISS_REASONS.map((r) => [r.id, r]),
) as Record<DismissReason, DismissReasonOption>

interface DismissButtonProps {
  onDismiss: (reason: DismissReason | null) => void
  disabled?: boolean
  /** When true, renders the wrap with `position: static` so it sits inline in a flex row
   *  rather than as a hover-overlay on the card. */
  inline?: boolean
  title?: string
}

const POPOVER_WIDTH = 240
const VIEWPORT_MARGIN = 8

export function DismissButton({
  onDismiss,
  disabled,
  inline,
  title = 'Dismiss this recommendation',
}: DismissButtonProps) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const btnRef = useRef<HTMLButtonElement | null>(null)
  const popRef = useRef<HTMLDivElement | null>(null)

  const place = useCallback(() => {
    const b = btnRef.current?.getBoundingClientRect()
    if (!b) return
    let left = b.right - POPOVER_WIDTH
    left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(left, window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN),
    )
    const popH = popRef.current?.offsetHeight ?? 300
    const below = b.bottom + 6
    const above = b.top - 6 - popH
    const fitsBelow = below + popH + VIEWPORT_MARGIN <= window.innerHeight
    const top = fitsBelow ? below : Math.max(VIEWPORT_MARGIN, above)
    setPos({ top, left })
  }, [])

  useEffect(() => {
    if (!open) return
    place()
    const raf = requestAnimationFrame(place)
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (popRef.current?.contains(t)) return
      if (btnRef.current?.contains(t)) return
      setOpen(false)
    }
    const onScroll = () => setOpen(false)
    document.addEventListener('mousedown', onDoc)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', place)
    return () => {
      cancelAnimationFrame(raf)
      document.removeEventListener('mousedown', onDoc)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', place)
    }
  }, [open, place])

  const pick = (reason: DismissReason | null) => {
    setOpen(false)
    onDismiss(reason)
  }

  const popover =
    open &&
    createPortal(
      <div
        ref={popRef}
        className="cg-dismiss-pop"
        role="menu"
        style={{ position: 'fixed', top: pos.top, left: pos.left }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cg-dismiss-pop-head">Why hide this?</div>
        {DISMISS_REASONS.map((r) => (
          <button
            key={r.id}
            type="button"
            className="cg-dismiss-pop-item"
            onClick={() => pick(r.id)}
          >
            <span className="cg-dismiss-pop-item-dot" data-reason={r.id} aria-hidden="true" />
            <div className="cg-dismiss-pop-item-text">
              <div className="cg-dismiss-pop-item-label">{r.label}</div>
              <div className="cg-dismiss-pop-item-desc">{r.desc}</div>
            </div>
          </button>
        ))}
        <div className="cg-dismiss-pop-divider" />
        <button
          type="button"
          className="cg-dismiss-pop-item subtle"
          onClick={() => pick(null)}
        >
          <div className="cg-dismiss-pop-item-text">
            <div className="cg-dismiss-pop-item-label">Just hide it</div>
            <div className="cg-dismiss-pop-item-desc">No reason — I'll decide later</div>
          </div>
        </button>
      </div>,
      document.body,
    )

  const wrapClassName = inline ? 'cg-dismiss-wrap inline' : 'cg-dismiss-wrap'

  return (
    <div className={wrapClassName} onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        type="button"
        className="cg-dismiss-btn"
        title={title}
        aria-label={title}
        data-open={open ? '' : undefined}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="x" size={11} />
      </button>
      {popover}
    </div>
  )
}
